'use client';

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { api, getAuthToken, getRefreshToken, isTokenExpired, logoutFully, readTokenExpiry } from '@/lib/api';
import { MeResponse } from '@/types';

export function useAuth() {
  const router = useRouter();
  const qc = useQueryClient();

  const query = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.get<MeResponse>('/api/profile/me');
      return data;
    },
    enabled: typeof window !== 'undefined' && !!getAuthToken(),
    retry: false,
  });

  // Re-fetch `me` when the user comes back to the tab — gives the silent
  // refresh interceptor a chance to recover an expired session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onFocus() {
      // Only re-validate if we still have *something* to refresh from.
      if (!getAuthToken() && !getRefreshToken()) return;
      qc.invalidateQueries({ queryKey: ['me'] });
    }
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
    };
  }, [qc]);

  // Silence "unused" warnings for these helpers — kept exported for callers
  // that still want the raw access-token expiry math.
  void readTokenExpiry; void isTokenExpired;

  async function logout() {
    await logoutFully();
    qc.clear();
    router.push('/login');
  }

  return {
    user: query.data,
    isLoading: query.isLoading,
    isAuthenticated: !!query.data,
    error: query.error,
    logout,
  };
}
